/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ColDef, ColumnState} from 'ag-grid-community';
import isNil from 'lodash.isnil';
import {getSearchtFilter} from '@ion/applications/src/globalsearch/GlobalSearchUtils';
import {useGridRefreshAction} from '@ion/applications/src/hooks/useGridRefreshAction';
import {useGroupRefresh} from '@ion/applications/src/hooks/useGroupRefresh';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {isRestricted} from '@ion/core/src/entities/LabelRulesValidator';
import {getUserRole} from '@ion/core/src/entities/Roles';
import {translate} from '@ion/core/src/locale/locale';
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import {
  AccountLink,
  CRUDPermissions,
  EnhancedUser,
  EnhancedUsers,
  Role,
  User,
  Users,
  UserType
} from '@ion/core/src/types/entities';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {getUniqItems} from '@ion/dls/src/editors/ListEditorUtils';
import {
  defaultDateFilterParams,
  defaultEqualsFilterParams,
  defaultTextFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {definitionsToState} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid,
  IonGridAPI
} from '@ion/dls/src/grid/IonGrid';
import {callbackInterceptor, SortModelType} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import {useGridEventListener} from '@ion/dls/src/grid/useGridEventListener';
import {useBulkOperationResults} from '@ion/dls/src/hooks/useBulkOperationResults';
import {useCRUDOperationResults} from '@ion/dls/src/hooks/useCRUDOperationResults';
import {GridPageSizeProps, ViewSubGroupsProps} from '@ion/dls/src/types/types';
import {
  determineColumnFilter,
  GridFilterType,
  processExportColumns
} from '@ion/dls/src/utils/GridUtils';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {exportUsers} from '../../../store/actions/exporter';
import {dispatchGridColumnsChange, sortColumns} from '../../../store/helpers';
import {gridDefaultPageSize} from '../../../utils/helpers';
import {AccountCell} from '../accounts/AccountCell';
import {GridOperations} from '../AdminContainer';
import {modularSearchSelector} from './selector';
import {
  adminUsersGridRefreshed,
  deleteAdminUsers,
  deleteUser,
  editAdminUsers,
  getAdminUsers,
  NAME,
  SET_GRID_COLUMNS
} from './state';
import {useAuthenticatedUser} from './useAuthenticatedUser';
import {UserRowEditor} from './UserRowEditor';
import {UsersActionMenu} from './UsersActionMenu';

export interface AdminUsersGridProps extends ViewSubGroupsProps, GridOperations, GridPageSizeProps {
  onSelectAccount?: (value: AccountLink) => void;
  searchKeyword?: string;
  includeGlobalSearch?: boolean;
  isCtcAdmin?: boolean;
  filteredColumns?: AdminUsersGridColumns[];
  enableSelectionsExport?: boolean;
  onUserMove?: boolean;
  usersPermissions?: CRUDPermissions;
  accountsPermissions?: CRUDPermissions;
  refreshInitialization?: Function;
  Features?: ApplicationFeatures;
}

export enum AdminUsersGridColumns {
  USERNAME = 'username',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  ROLE = 'roleUI',
  GROUP = 'groupUI',
  STATUS = 'status',
  UNIT_OF_MEASURE = 'unitOfMeasure',
  USER_TYPE = 'usertype',
  CREATED = 'createdOnUI',
  LAST_MODIFIED = 'lastModifiedOnUI',
}

const DEFAULT_FILTERED_COLUMNS = [
  AdminUsersGridColumns.USERNAME,
  AdminUsersGridColumns.FIRST_NAME,
  AdminUsersGridColumns.LAST_NAME,
  AdminUsersGridColumns.GROUP,
  AdminUsersGridColumns.STATUS,
  AdminUsersGridColumns.CREATED
];

/**
 * @AdminUsersGrid
 *
 * Grid to be rendered on the Admin Users page.
 */
export const AdminUsersGrid = ({
  onSelectAccount,
  viewSubGroups,
  toggleViewSubGroups,
  isCtcAdmin,
  setGridPageSize,
  setTotalRowsCount,
  Features,
  usersPermissions,
  accountsPermissions,
  refreshInitialization,
  searchKeyword = '',
  gridPageSize = gridDefaultPageSize,
  filteredColumns = DEFAULT_FILTERED_COLUMNS,
  includeGlobalSearch = false,
  enableSelectionsExport = false,
  hideSubGroups = false,
  isSortByAlphaNumeric = false,
  onUserMove = false,
  usePreferedPageSize = false
}: AdminUsersGridProps, ref) => {
  const dispatch            = useDispatch();
  const modularSearchParams = useSelector(modularSearchSelector);
  const columnState         = useSelector<ColDef[]>(({adminUsers: {grid}}) => grid?.columnState);

  const {
    addResult,
    editResult,
    deleteResult
  } = useCRUDOperationResults(NAME);

  const {
    bulkEditResult,
    bulkDeleteResult,
    bulkImportResult,
    bulkMoveResult
  } = useBulkOperationResults(NAME);

  const user = useAuthenticatedUser();

  const [refreshRequired, setRefreshRequired] = useState(false);
  const [selectedUsers, setSelectedUsers]     = useState([]);
  const refreshGrid                           = useGroupRefresh();
  const ionGridRef                            = useRef<IonGridAPI>();

  const defaultColumnDefinitions = useMemo(() =>
    getColumnDefinitions(includeGlobalSearch, filteredColumns, isSortByAlphaNumeric),
  [includeGlobalSearch, filteredColumns]);

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : defaultColumnDefinitions,
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  useGridRefreshAction(refreshRequired, adminUsersGridRefreshed);
  useGridEventListener(ionGridRef, 'selectionChanged', onSelectionChanged);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    toggleColumnChooser,
    onExport,
    onRefresh
  }));

  const onRefresh = function() {
    setRefreshRequired(true);
  };

  const onColumnSelectionsUpdate = function(columns: ColDef[]): void {
    onColumnChooserSave(columns);
    dispatch(dispatchGridColumnsChange(definitionsToState(columns), NAME, SET_GRID_COLUMNS));
  };

  const onColumnStateUpdate = function(state: ColumnState[]): void {
    if (!includeGlobalSearch) {
      dispatch(dispatchGridColumnsChange(state, NAME, SET_GRID_COLUMNS));
    }
  };

  useEffect(() => {
    const refresh = Boolean(
      addResult ||
      editResult ||
      deleteResult ||
      bulkEditResult ||
      bulkDeleteResult ||
      bulkImportResult ||
      bulkMoveResult
    );
    setRefreshRequired(refresh);
  }, [addResult, deleteResult, editResult, bulkEditResult, bulkDeleteResult, bulkImportResult, bulkMoveResult]);

  useEffect(() => {
    if (includeGlobalSearch && !isNil(searchKeyword)) {
      setRefreshRequired(!refreshRequired);
    }
  }, [includeGlobalSearch, searchKeyword]);

  const currentRole = EntityLabel.getRole();
  
  const attributeMappings = {
    groupUI          : isRestricted(currentRole) ? 'account' : 'account.name',
    roleUI           : 'role.name',
    createdOnUI      : 'createdOn',
    lastModifiedOnUI : 'lastModifiedOn'
  };

  const defaultSort: SortModelType = {
    colId : 'username',
    sort  : 'asc'
  };

  const getFilter = (filter) => {
    if (includeGlobalSearch) {
      return getSearchtFilter('username', searchKeyword, filter);
    }
    return filter;
  };

  function getUsers({
    page,
    pageSize,
    sort,
    filter,
    callback
  }) {
    dispatch(getAdminUsers({
      includeSubGroups    : viewSubGroups as boolean,
      includeGlobalSearch : includeGlobalSearch,
      callback            : callbackInterceptor(callback, setTotalRowsCount),
      filter              : getFilter(filter),
      pageId              : page,
      pageSize,
      sort
    }));
  };

  function onEditUser(users: EnhancedUsers) {
    dispatch(editAdminUsers(users));
  }

  function onDeleteUser(user: EnhancedUser) {
    dispatch(deleteUser(user));
  }

  function onDeleteUsers(users: User[]) {
    dispatch(deleteAdminUsers(users));
  }

  const onExport = function(downloadParams?: DownloadTemplate) {
    const params = ionGridRef.current?.getExportParams(
      viewSubGroups,
      enableSelectionsExport,
      createModularSearchFilters(modularSearchParams)
    );
    if (downloadParams?.columns) {
      params.columns = processExportColumns(downloadParams?.columns);
    }
    dispatch(exportUsers(params, downloadParams?.columns ? downloadParams : undefined));
  };

  function checkBulkActionDiabledStatus(selections: Users) {
    return selections?.filter(({id}) => id === user?.id)?.length > 0;
  }

  const onSelectionChanged = useCallback((event) => {
    const rowsSelected = event.api.getSelectedRows();
    setSelectedUsers(getUniqItems(rowsSelected));
  }, []);

  const getActionMenu = useMemo(() => (
    <UsersActionMenu selectedUsers={selectedUsers}
      disableAction={ !usersPermissions?.hasUpdatePermission} />
  ), [selectedUsers]);

  return (
    <IonGrid ref={ionGridRef}
      paginationEnabled
      showCustomPaginationBar
      pageSize={gridPageSize}
      setGridPageSize={setGridPageSize}
      refreshRequired={refreshRequired || (refreshGrid && !includeGlobalSearch)}
      setRefreshRequired={setRefreshRequired}
      loadItems={getUsers}
      augmentItems={constructUsersGridRows}
      attributeMappings={attributeMappings}
      checkboxesEnabled={!includeGlobalSearch}
      onBulkEdit={onEditUser}
      onBulkDelete={onDeleteUsers}
      actionMenuItems={onUserMove ? getActionMenu : null}
      columnDefinitions={columnDefinitions}
      defaultDefinitions={defaultDefinitions}
      showColumnChooser={showColumnChooser}
      disableBulkDelete={!usersPermissions?.hasUpdatePermission}
      disableBulkEdit={ !usersPermissions?.hasUpdatePermission}
      onColumnChooserSave={onColumnSelectionsUpdate}
      onColumnsUpdated={onColumnStateUpdate}
      onColumnChooserClose={function() {
        toggleColumnChooser(false);
      }}
      columnComponents={{
        rowMenu : item => (
          <UserRowEditor user={item?.data}
            includeGlobalSearch={includeGlobalSearch}
            usersPermissions={usersPermissions} />
        ),
        accountName : (item: GridResult) => (
          <AccountCell item={item}
            isCtcAdmin={isCtcAdmin}
            Features={Features}
            accountsPermissions={accountsPermissions}
            refreshInitialization={refreshInitialization}
            onSelectAccount={onSelectAccount} />
        )
      }}
      pageOffset
      pageSizes={PAGE_SIZES}
      hideSubGroups={hideSubGroups}
      toggleViewSubGroups={!includeGlobalSearch ? toggleViewSubGroups : undefined}
      viewSubGroups={viewSubGroups}
      defaultSort={defaultSort}
      gridOptions={{onSelectionChanged}}
      checkBulkActionDiabledStatus={checkBulkActionDiabledStatus}
      usePreferedPageSize={usePreferedPageSize} />
  );
};

/**
 * @constructUsersGridRows
 *
 * Converts a collection of users and converts them to a list of items specific
 * for display within the Admin Users Grid, while retaining all of the items existing
 * attributes,
 */
export const constructUsersGridRows = (users: User[], roles: Role[]): GridResult[] => {
  return users.map((user: User) => {
    const roleUI = roles?.length ? getUserRole(user, roles, 'name') : '';
    return {
      ...user,
      groupUI          : EntityLabel.get(user?.account),
      createdOnUI      : formatDateTime(user?.createdOn),
      lastModifiedOnUI : formatDateTime(user?.lastModifiedOn),
      roleUI
    };
  }) as GridResult[];
};

const filterColumns = (columns, isSortByAlphaNumeric) => isSortByAlphaNumeric ? sortColumns(columns) : columns;

/**
 * @getColumnDefinitionsDesktop
 *
 * Returns column definitions for the desktop grid implementation ...
 */
const getColumnDefinitionsDesktop = (
  filteredColumns: AdminUsersGridColumns[],
  isSortByAlphaNumeric: boolean
): ColDef[] => {
  const columns: ColDef[] = [{
    headerName   : translate('admin-users:grid.columns.username'),
    field        : AdminUsersGridColumns.USERNAME,
    sort         : 'asc',
    sortable     : true,
    filter       : determineColumnFilter(AdminUsersGridColumns.USERNAME, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  }, {
    headerName   : translate('admin-users:grid.columns.firstName'),
    field        : AdminUsersGridColumns.FIRST_NAME,
    sortable     : true,
    filter       : determineColumnFilter(AdminUsersGridColumns.FIRST_NAME, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  }, {
    headerName   : translate('admin-users:grid.columns.lastName'),
    field        : AdminUsersGridColumns.LAST_NAME,
    sortable     : true,
    filter       : determineColumnFilter(AdminUsersGridColumns.LAST_NAME, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  }, {
    headerName : translate('admin-users:grid.columns.email'),
    field      : AdminUsersGridColumns.EMAIL,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('admin-users:grid.columns.role'),
    field      : AdminUsersGridColumns.ROLE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName         : translate('admin-users:grid.columns.group'),
    field              : AdminUsersGridColumns.GROUP,
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : determineColumnFilter(AdminUsersGridColumns.GROUP, filteredColumns),
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  }, {
    headerName   : translate('admin-users:grid.columns.status'),
    field        : AdminUsersGridColumns.STATUS,
    sortable     : true,
    filter       : determineColumnFilter(AdminUsersGridColumns.STATUS, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultEqualsFilterParams
  }, {
    headerName : translate('admin-users:grid.columns.unitOfMeasure'),
    field      : AdminUsersGridColumns.UNIT_OF_MEASURE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  }, {
    headerName     : translate('admin-users:grid.columns.userType'),
    field          : AdminUsersGridColumns.USER_TYPE,
    valueFormatter : ({value}) => value === UserType.M2M
      ? translate('common:editors.userType.m2mLabel') : '',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  }, {
    headerName   : translate('admin-users:grid.columns.createdOn'),
    field        : AdminUsersGridColumns.CREATED,
    sortable     : true,
    filter       : determineColumnFilter(AdminUsersGridColumns.CREATED, filteredColumns, GridFilterType.DATE),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultDateFilterParams
  }, {
    headerName   : translate('admin-users:grid.columns.lastModifiedOn'),
    field        : 'lastModifiedOnUI',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultDateFilterParams
  }, {
    headerName : translate('admin-users:grid.columns.description'),
    field      : 'description',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  }];
  return filterColumns(columns, isSortByAlphaNumeric);
};

/**
 * @getColumnDefinitionsMobile
 *
 * Returns column definitions for mobile grid implementation ...
 */
const getColumnDefinitionsMobile = (isSortByAlphaNumeric: boolean): ColDef[] => {
  const columns: ColDef[] = [{
    headerName   : translate('admin-users:grid.columns.username'),
    field        : 'username',
    sort         : 'asc',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  }, {
    headerName   : translate('admin-users:grid.columns.firstName'),
    field        : 'firstName',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  }, {
    headerName   : translate('admin-users:grid.columns.lastName'),
    field        : 'lastName',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  }, {
    headerName : translate('admin-users:grid.columns.email'),
    field      : 'email',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('admin-users:grid.columns.role'),
    field      : 'roleUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName         : translate('admin-users:grid.columns.group'),
    field              : 'groupUI',
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  }, {
    headerName   : translate('admin-users:grid.columns.status'),
    field        : 'status',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultEqualsFilterParams
  }, {
    headerName : translate('admin-users:grid.columns.unitOfMeasure'),
    field      : 'unitOfMeasure',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  }, {
    headerName     : translate('admin-users:grid.columns.userType'),
    field          : 'usertype',
    valueFormatter : ({value}) => value === UserType.M2M
      ? translate('common:editors.userType.m2mLabel') : '',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  }, {
    headerName   : translate('admin-users:grid.columns.createdOn'),
    field        : 'createdOnUI',
    sortable     : true,
    filter       : 'date',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultDateFilterParams
  }, {
    headerName   : translate('admin-users:grid.columns.lastModifiedOn'),
    field        : 'lastModifiedOnUI',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultDateFilterParams
  }, {
    headerName : translate('admin-users:grid.columns.description'),
    field      : 'description',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  }];
  return filterColumns(columns, isSortByAlphaNumeric);
};

const getColumnDefinitionsDesktopSearch = (): ColDef[] => {
  return [{
    headerName   : translate('admin-users:grid.columns.username'),
    field        : 'username',
    sort         : 'asc',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  }, {
    headerName   : translate('admin-users:grid.columns.firstName'),
    field        : 'firstName',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  }, {
    headerName   : translate('admin-users:grid.columns.lastName'),
    field        : 'lastName',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  }, {
    headerName   : translate('admin-users:grid.columns.status'),
    field        : 'status',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultEqualsFilterParams
  }, {
    headerName         : translate('admin-users:grid.columns.group'),
    field              : 'groupUI',
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  }];
};

const getColumnDefinitions = (
  includeGlobalSearch: boolean,
  filteredColumns: AdminUsersGridColumns[],
  isSortByAlphaNumeric: boolean
) => {
  if (!includeGlobalSearch) {
    return {
      desktop : getColumnDefinitionsDesktop(filteredColumns, isSortByAlphaNumeric),
      mobile  : getColumnDefinitionsMobile(isSortByAlphaNumeric)
    };
  }
  const definitions = getColumnDefinitionsDesktopSearch();
  return {desktop: definitions, mobile: definitions};
};

export default forwardRef(AdminUsersGrid);
